import { marked, type Token } from 'marked';

const decodeEntities = (value: string): string => value.replace(/&(?:#\d+|#x[\da-f]+|[a-z]+);/gi, entity => {
  // Decode only a single character reference, never parse authored HTML.
  const element = document.createElement('textarea');
  element.innerHTML = entity;
  return element.value;
});

const htmlText = (value: string): string => {
  // Parse in a detached, inert document and return text only. Keep authored
  // script/style contents as text, including when encoded literals are passed
  // through the editor again. This is plain-text extraction, not HTML output.
  const parsed = new DOMParser().parseFromString(value, 'text/html');
  parsed.querySelectorAll('br').forEach(element => element.replaceWith('\n'));
  return parsed.documentElement.textContent ?? '';
};

/** Plain display of an existing capture; callers decide whether an edit is saved. */
export function plainJustificationText(value: string): string {
  const render = (tokens: Token[]): string => tokens.map(token => {
    switch (token.type) {
      case 'space': case 'br': return '\n';
      case 'hr': case 'def': case 'checkbox': return '';
      case 'heading': case 'paragraph': case 'blockquote':
        return `${render(token.tokens ?? [])}\n\n`;
      case 'strong': case 'em': case 'del': return render(token.tokens ?? []);
      case 'link': case 'image': {
        const label = render(token.tokens ?? []) || decodeEntities(String(token.text ?? ''));
        const href = decodeEntities(String(token.href ?? ''));
        return !href || label === href ? label : `${label} (${href})`;
      }
      case 'list': return `${token.items.map((item: Token) => render([item]).trim()).join('\n')}\n\n`;
      case 'list_item': return render(token.tokens ?? []);
      case 'table': {
        const row = (cells: Array<{ tokens: Token[] }>) => cells.map(cell => render(cell.tokens)).join(' / ');
        return `${[row(token.header), ...token.rows.map(row)].join('\n')}\n\n`;
      }
      case 'html': return htmlText(String(token.text ?? ''));
      case 'code': return `${decodeEntities(String(token.text ?? ''))}\n\n`;
      default: return 'tokens' in token && Array.isArray(token.tokens)
        ? render(token.tokens)
        : 'text' in token ? decodeEntities(String(token.text ?? '')) : '';
    }
  }).join('');
  const trailingWhitespace = value.match(/\s*$/)?.[0] ?? '';
  return render(marked.lexer(value)).replace(/\n{3,}/g, '\n\n').trimEnd() + trailingWhitespace;
}
